import { useEffect, useState } from "react";

import type { LanguageFilterChipOption } from "../LanguageFilterChip";
import type {
  LandingPageLink,
  OfferLandingPages,
  TargetPageTranslations,
} from "./types";

import { Button as ButtonV2 } from "../Button";
import { useIsMobile } from "../hooks/useIsMobile";
import { LanguageFilterChip } from "../LanguageFilterChip";
import { ResponsiveModal } from "../ResponsiveModal";
import { Typo } from "../Typo";
import { pickDefaultLandingPage } from "./pickDefaultLandingPage";
import { TargetPageCard } from "./TargetPageCard";

type TargetPageModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  onConfirm: (selection: {
    offer: OfferLandingPages;
    landingPage: LandingPageLink;
  }) => void;
  selectedOffer: OfferLandingPages;
  selectedLandingPage: LandingPageLink;
  offerPages: OfferLandingPages[];
  translations: TargetPageTranslations;
  getLanguageLabelFromCode: (code: string) => string;
};

export const TargetPageModal = ({
  isOpen,
  onClose,
  onBack,
  onConfirm,
  selectedOffer,
  selectedLandingPage,
  offerPages,
  translations,
  getLanguageLabelFromCode,
}: TargetPageModalProps) => {
  const isMobile = useIsMobile();

  const [selectedOfferId, setSelectedOfferId] = useState<string | undefined>(
    selectedOffer.id,
  );
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    selectedLandingPage.languageCode ?? "en",
  );
  const [languageFilters, setLanguageFilters] = useState<string[]>([]);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedOfferId(selectedOffer.id);
    setSelectedLanguage(selectedLandingPage.languageCode ?? "en");
    setLanguageFilters([]);
  }, [isOpen, selectedOffer.id, selectedLandingPage.languageCode]);

  const offersWithLandingPages = offerPages.filter(
    (offer) => (offer.landingPages?.length ?? 0) > 0,
  );

  const allLanguageCodes = (() => {
    const codes = new Set<string>();
    offersWithLandingPages.forEach((offer) =>
      (offer.landingPages ?? []).forEach((lp) => codes.add(lp.languageCode)),
    );
    return Array.from(codes);
  })();

  const languageFilterOptions: LanguageFilterChipOption[] = allLanguageCodes
    .map((code) => ({
      value: code,
      label: getLanguageLabelFromCode(code),
    }))
    .sort((a, b) => {
      if (a.value === "en" && b.value !== "en") return -1;
      if (a.value !== "en" && b.value === "en") return 1;

      return a.label.localeCompare(b.label);
    });

  const languageFilterTranslations = {
    label: translations.languageFilterLabel,
    multipleSelected: translations.languageFilterMultiple,
  };

  const filteredOffers = (() => {
    if (languageFilters.length === 0) return offersWithLandingPages;
    return offersWithLandingPages.filter((offer) => {
      if (offer.id === selectedOfferId) return true;
      return (offer.landingPages ?? []).some((lp) =>
        languageFilters.includes(lp.languageCode),
      );
    });
  })();

  const handleConfirm = () => {
    const picked = offersWithLandingPages.find(
      (offer) => offer.id === selectedOfferId,
    );
    if (!picked) {
      if (selectedOffer && selectedLandingPage) {
        onConfirm({
          offer: selectedOffer,
          landingPage: selectedLandingPage,
        });
      }
      return;
    }

    const defaultLandingPage = pickDefaultLandingPage(picked, selectedLanguage);

    if (!defaultLandingPage) return;

    onConfirm({
      offer: picked,
      landingPage: defaultLandingPage,
    });
  };

  const handleSelectOffer = (offer: OfferLandingPages) => {
    if (offer.id === selectedOfferId) return;

    setSelectedOfferId(offer.id);
    const defaultLanguage = pickDefaultLandingPage(offer)?.languageCode ?? "en";
    setSelectedLanguage(defaultLanguage);
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      onBack={onBack}
      isMobile={isMobile}
      maxWidth={800}
      maxHeight={800}
      backLabel={isMobile ? translations.title : translations.back}
      modalClassName="!p-0"
      headerClassName="px-8 pt-8 mb-4"
      contentContainerClassName="flex min-h-0 flex-1 overflow-hidden"
      footer={
        isMobile && (
          <div className="flex items-stretch justify-end lg:hidden">
            <ButtonV2
              data-testid="target-page-confirm"
              variant="brand"
              onClick={handleConfirm}
              disabled={!selectedOfferId}
              className="flex flex-1"
            >
              {translations.confirm}
            </ButtonV2>
          </div>
        )
      }
      isDismissable={false}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 lg:gap-6">
        {!isMobile && (
          <div className="hidden flex-col items-start gap-4 lg:flex">
            <Typo type="heading2XL">{translations.title}</Typo>
            <Typo type="bodySM" textColor="text-text-secondary">
              {translations.subLabel}
            </Typo>
          </div>
        )}

        <div
          className="flex flex-col items-start gap-6"
          data-testid="target-page-language-filter-container"
        >
          {allLanguageCodes.length > 1 && (
            <LanguageFilterChip
              options={languageFilterOptions}
              selectedValues={languageFilters}
              onChange={setLanguageFilters}
              translations={languageFilterTranslations}
              data-testid="target-page-language-filter"
            />
          )}
        </div>

        <span className="flex min-h-0 flex-1 flex-col items-stretch gap-0">
          {filteredOffers.length > 0 && (
            <div
              data-testid="target-page-list"
              className="grid min-h-0 flex-1 grid-cols-1 gap-6 overflow-y-auto pb-8 lg:grid-cols-2 lg:pb-0"
            >
              {filteredOffers.map((offer) => (
                <TargetPageCard
                  key={offer.id}
                  offer={offer}
                  isSelected={selectedOfferId === offer.id}
                  selectedLanguage={selectedLanguage}
                  onSelect={() => handleSelectOffer(offer)}
                  onLanguageChange={setSelectedLanguage}
                  translations={{
                    languageLabel: translations.languageFilterLabel,
                    languagesCount: translations.languagesCount,
                    previewLabel: translations.previewLabel,
                    clearFilterLabel: translations.clearFilter,
                    noAvailableLanguagesLabel:
                      translations.notAvailableLanguages,
                  }}
                  languageFilters={languageFilters}
                  onClearFilter={() => setLanguageFilters([])}
                  getLanguageLabelFromCode={getLanguageLabelFromCode}
                />
              ))}
            </div>
          )}
          {!isMobile && (
            <div className="flex items-stretch justify-end py-4">
              <ButtonV2
                data-testid="target-page-confirm"
                variant="brand"
                onClick={handleConfirm}
                disabled={!selectedOfferId}
              >
                {translations.confirm}
              </ButtonV2>
            </div>
          )}
        </span>
      </div>
    </ResponsiveModal>
  );
};
